import { useEffect, type RefObject } from 'react';

/**
 * Calls `onClose` on a press outside `ref` or on Escape, while `open`. On Escape the focus goes
 * back to `restoreFocus` (the control that opened the popup). Used by the top bar's menus.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
  restoreFocus?: RefObject<HTMLElement | null>
) {
  useEffect(() => {
    if (!open) return;
    const onPress = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
      restoreFocus?.current?.focus();
    };
    document.addEventListener('mousedown', onPress);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, onClose, restoreFocus]);
}
